import { useQuery } from '@apollo/client';
import { GetOversightAssessmentCountDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useIsModuleEnabled } from 'src/hooks/useIsModuleEnabled';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

/**
 * Gates the optional Oversight rating columns on the Risk Register and Compliance Register.
 *
 * Returns true when either:
 *   - the user's role carries `read:oversight_assessment` AND the
 *     Oversight module is enabled for the org, OR
 *   - the user is owner/contributor on at least one oversight assessment
 *     (visible via Hasura row-level perms). In this case the module flag is
 *     intentionally ignored: if the user is already assigned to a CMA, the
 *     data exists for them and they should be able to surface its rating
 *     regardless of the org's module toggle.
 *
 * While the permission query is still loading, the aggregate count query is
 * deferred so role-permission users (who don't need the count) don't fire it
 * unnecessarily. Returns `false` during loading — column config will hydrate
 * once the decision is stable.
 */
export const useCanViewOversightColumns = (): boolean => {
  const oversightModuleEnabled = useIsModuleEnabled(
    'assessment.subModules.oversight_register_and_findings'
  );

  const { hasPermission: hasRolePermission, loading: permissionLoading } =
    useHasPermissionQuery('read:oversight_assessment');

  const roleGrants = hasRolePermission && oversightModuleEnabled;

  // Only probe for contributor rows if the role can select oversight assessments
  // at all — otherwise the aggregate 404s with `field 'oversight_assessment' not
  // found in type: 'query_root'` for roles that can't read it (RSP-6542).
  // role_access still tracks this entity under its legacy
  // `compliance_monitoring_assessment` name (the cma->oversight rename left the
  // catalogue on the old value, RSP-5941/5942), so gate on that. `true` =
  // "role has ANY oversight read grant" (owner/contributor/any).
  const { hasPermission: canSelectOversight, loading: oversightSelectLoading } =
    useHasPermissionQuery(
      'read:compliance_monitoring_assessment',
      undefined,
      true
    );

  const { data } = useQuery(GetOversightAssessmentCountDocument, {
    skip:
      permissionLoading ||
      oversightSelectLoading ||
      roleGrants ||
      !canSelectOversight,
    fetchPolicy: 'cache-first',
  });
  const hasContributions =
    (data?.oversight_assessment_aggregate.aggregate?.count ?? 0) > 0;

  if (permissionLoading) {
    return false;
  }

  return roleGrants || hasContributions;
};
